import { useQuery } from '@tanstack/react-query';
import api from '../../../services/api';
import type { FilterCondition } from '../../../types/bi';

export interface FieldCategory {
  id: number;
  name: string;
  slug?: string | null;
  fields: FieldEntry[];
}

export interface FieldEntry {
  field_key: string;
  field_name: string;
  field_type: string;
  source_table: string;
  db_type: string;
  options?: Array<{
    value: string;
    label: string;
  }>;
}

export interface SemanticEntity {
  key: string;
  label: string;
  grain: string;
  description: string;
}

export interface SemanticDerivedField {
  key: string;
  label: string;
  source_field: string;
  expression: string;
  role_compatibility: string[];
}

export interface SemanticCatalogSummary {
  catalog_version: string;
  entity_count: number;
  derived_field_count: number;
  curated_measure_count: number;
  featured_measure_keys: string[];
  entities: SemanticEntity[];
  derived_fields: SemanticDerivedField[];
  curated_measures?: CuratedMeasure[];
}

export interface CuratedMeasure {
  key: string;
  label: string;
  description: string;
  field_key: string;
  aggregation: string;
  default_format: 'number' | 'currency' | 'percentage';
  valid_widget_types: string[];
  valid_visual_slots: string[];
  filters?: Array<{
    field_key: string;
    operator: FilterCondition['operator'];
    value: unknown;
  }>;
  derived_from?: string[];
}

export interface SemanticFieldHint {
  field_key: string;
  field_name: string;
  field_type: string;
  db_type: string;
  source_table: string;
  role_compatibility: string[];
  default_agg_hint: string;
  null_handling_hint: string;
  cardinality_hint: string;
  semantic_kind: 'measure_candidate' | 'dimension_candidate';
}

export interface BIFieldsResponse {
  categories: FieldCategory[];
  semantic_field_catalog?: SemanticFieldHint[];
  semantic_catalog?: SemanticCatalogSummary;
}

export function useBIFields() {
  return useQuery<BIFieldsResponse>({
    queryKey: ['bi', 'query', 'fields'],
    queryFn: () => api.get('/api/v1/bi/query/fields').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
  });
}
